import React, { createContext, useReducer, useContext } from "react";
import AuthReducer from "../reducers/authReducer";
import axios from "../../axios";
import * as actionTypes from "../actionTypes";
import * as urlRequest from "../urls";
import { AlertContext } from "./alertState";

const token = localStorage.getItem("token");

// InitialState
const initialState = {
  isAuthenticated: false,
  token: token,

  currentUser: null,
  sidebar: [],
  permissionKeys: [],

  error: null,
  loading: false,
  authChecked: false,

  photoLoad: false,
  photo: "",
  photoURL: null,
};

// creating context
export const AuthContext = createContext(initialState);

// provider component
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(AuthReducer, initialState);
  const { alertDispatch } = useContext(AlertContext);

  // actions
  const handleLogin = async (user) => {
    dispatch({ type: actionTypes.AUTH_START });
    localStorage.setItem("userType", "user");

    try {
      const result = await axios.post(urlRequest.Login, user);
      const userObj_serialized = JSON.stringify(result.data);
      const token = result.data.token;
      storeResponseDataHandler(token, userObj_serialized);

      if (result.data.success) {
        alertDispatch({
          type: "open",
          message: `Successfully logged in`,
        });

        // SET HEADER IMMEDIATELY
        axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

        // LOAD USER
        await getMeHandler();

        dispatch({
          type: actionTypes.AUTH_CHECKED,
        });
      }
      return result.data.success;
    } catch (error) {
      await dispatch({ type: actionTypes.AUTH_FAIL, payload: "logout failed" });
      console.log(error);
      return false;
    }
  };

  // actions
  const handleInterpreterLogin = async (user) => {
    dispatch({ type: actionTypes.AUTH_START });
    localStorage.setItem("userType", "interpreter");

    try {
      const result = await axios.post(urlRequest.InterpreterLogin, user);
      const userObj_serialized = JSON.stringify(result.data);
      const token = result.data.token;
      storeResponseDataHandler(token, userObj_serialized);

      if (result.data.success) {
        alertDispatch({ type: "open", message: `Successfully logged in` });
        axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

        await getInterpreterMeHandler();

        dispatch({
          type: actionTypes.AUTH_CHECKED,
        });
      }
      return result.data.success;
    } catch (error) {
      await dispatch({ type: actionTypes.AUTH_FAIL, payload: "logout failed" });
      console.log(error);
      return false;
    }
  };

  const getInterpreterMeHandler = async () => {
    const { data } = await axios.get(urlRequest.InterpreterMe);

    localStorage.setItem("interpreterRef", data.data.int_id);
    localStorage.setItem("intID", data.data._id);
    localStorage.setItem("loggedIn", data.data._id);

    dispatch({
      type: actionTypes.SET_USER_DETAILS,
      payload: {
        user: {
          ...data.data,
          role: "interpreter",
        },
        sidebar: [],
        permissionKeys: [],
      },
    });
  };

  const getMeHandler = async () => {
    const { data } = await axios.get(urlRequest.LoggedInMe);
    // console.log("getMeHandler: ", data);

    localStorage.setItem("loggedIn", data.data.user._id);

    // update context state
    dispatch({
      type: actionTypes.SET_USER_DETAILS,
      payload: data.data,
    });
  };

  const storeResponseDataHandler = (token) => {
    localStorage.setItem("token", token);

    dispatch({
      type: actionTypes.AUTH_SUCCESS,
      payload: token,
    });
  };

  // const storeResponseDataHandler = (token, response) => {
  //   localStorage.setItem("token", token);
  //   localStorage.setItem("user", response);
  //   const userObj_deserialized = JSON.parse(localStorage.getItem("user"));
  //   dispatch({
  //     type: actionTypes.AUTH_SUCCESS,
  //     payload: userObj_deserialized.token,
  //   });
  //   dispatch({
  //     type: actionTypes.SET_USER_DETAILS,
  //     payload: userObj_deserialized,
  //   });
  // };

  const getAccessToken = () => {
    if (localStorage.getItem("token")) {
      const token = localStorage.getItem("token");
      return token;
    } else {
      return null;
    }
  };

  const getUserDetails = () => {
    if (localStorage.getItem("user")) {
      const user = JSON.parse(localStorage.getItem("user"));
      return user;
    } else {
      return null;
    }
  };

  const handleLogout = async () => {
    dispatch({ type: actionTypes.AUTH_START });

    // Determine account type BEFORE clearing localStorage.
    const isInterpreter = Boolean(
      localStorage.getItem("intID") || localStorage.getItem("interpreterRef"),
    );

    try {
      const logoutUrl = isInterpreter
        ? "/api/v1/auth/intlogout"
        : "/api/v1/auth/logout";

      await axios.post(logoutUrl);
    } catch (error) {
      console.error(
        "Backend logout/activity logging failed:",
        error.response?.data || error.message,
      );
    } finally {
      // Always clear local credentials, even if the API fails.
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      localStorage.removeItem("intID");
      localStorage.removeItem("interpreterRef");
      localStorage.removeItem("loggedIn");

      delete axios.defaults.headers.common["Authorization"];

      dispatch({ type: actionTypes.AUTH_LOGOUT });

      alertDispatch({
        type: "open",
        message: "Successfully logged out",
      });
    }
  };

  const authHandler = async () => {
    const token = getAccessToken();

    if (!token) {
      dispatch({
        type: actionTypes.AUTH_CHECKED,
      });
      return;
    }

    try {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

      const userType = localStorage.getItem("userType");

      if (userType === "interpreter") {
        await getInterpreterMeHandler();
      } else {
        await getMeHandler();
      }
    } catch (err) {
      localStorage.removeItem("token");
      localStorage.removeItem("userType");

      delete axios.defaults.headers.common["Authorization"];

      dispatch({
        type: actionTypes.AUTH_LOGOUT,
      });
    } finally {
      dispatch({
        type: actionTypes.AUTH_CHECKED,
      });
    }
  };

  const getUserPermissions = async (userId) => {
    try {
      const { data } = await axios.get(
        `${urlRequest.User}/${userId}/page-permissions/tree`,
      );

      return data.data || [];
    } catch (err) {
      console.error("Failed to load user permissions", err);
      return [];
    }
  };

  const getAllPermissions = async () => {
    try {
      const { data } = await axios.get("/api/v1/pages");

      console.log("ALL PAGES:", data);

      return data.pages || [];
    } catch (err) {
      console.error("Failed to load pages", err);
      return [];
    }
  };

  /* Get Profile image for interpreter by id */
  const getProfileHandler = async (filename) => {
    // console.log('photoname: ', filename);
    if (filename === "") return false;
    await dispatch({ type: "FILE_UPLOAD_START" });
    try {
      const photo = await axios.get(
        `${urlRequest.Interpreters}/${filename}/photo`,
      );
      dispatch({ type: "INTERPRETER_USER_PHOTO", payload: photo.config.url });
      // console.log('PHOTO: ', photo);
    } catch (error) {
      console.log(error);
      dispatch({ type: actionTypes.FETCH_ERRORS, payload: error });
    }
  };

  return (
    <AuthContext.Provider
      value={{
        loading: state.loading,
        error: state.error,

        isAuthenticated: state.isAuthenticated,

        token: state.token,

        currentUser: state.currentUser,
        sidebar: state.sidebar,
        permissionKeys: state.permissionKeys,
        authChecked: state.authChecked,

        photo: state.photo,

        handleLogin,
        handleInterpreterLogin,
        handleLogout,
        authHandler,

        isLoad: state.photoLoad,
        photoURL: state.photoURL,

        getProfileHandler,
        getUserPermissions,
        getAllPermissions,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
